// SVG export: a standalone document mirroring the canvas rendering (same
// layouts, stroke curves, connector routes). Pure, so it's unit-tested.
import type { BoardObject, Connector } from '../../shared/types.ts';
import { sanitizeHref } from '../../shared/rich-text.ts';
import { CARD } from '../../shared/cards.ts';
import { AUDIO, formatDuration } from '../../shared/audio.ts';
import { AUDIO_LAYOUT, waveBars } from '../engine/draw/audio.ts';
import { BOX_BORDER, BOX_DONE, CHIP_FONT_SIZE, chipIconAt, type CardFace } from '../cards/face.ts';
import { arrowPoints, CONNECTOR_WIDTH, connectorPoints } from '../engine/connectors.ts';
import { strokeCurve } from '../engine/freehand.ts';
import { FRAME_TITLE_GAP_PX, FRAME_TITLE_PX } from '../engine/draw/content.ts';
import { BULLET, bulletX, FONT_FAMILY, type TextLayout } from '../text/layout.ts';
import { LINK_COLOR, type PlacedText } from '../text/placement.ts';
import type { Rect } from '../util/geometry.ts';

export interface SvgDeps {
  get: (id: string) => BoardObject | undefined;
  layoutOf: (o: BoardObject) => PlacedText | null;
  /** A URL (usually a data: URL) for an image asset, or null to draw a placeholder. */
  imageHref: (hash: string) => string | null;
}

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');
const n = (v: number) => String(Math.round(v * 100) / 100);
const pts = (list: readonly (readonly [number, number])[]) => list.map(([x, y]) => `${n(x)},${n(y)}`).join(' ');

function text(p: PlacedText, clip: string | null): string {
  return layoutSvg(p.layout, p.ox, p.oy, p.color, clip);
}

function layoutSvg(layout: TextLayout, ox: number, oy: number, color: string, clip: string | null): string {
  const size = layout.fontSize;
  const out: string[] = [];
  for (const ln of layout.lines) {
    const y = n(oy + ln.y);
    if (ln.bullet) out.push(`<text x="${n(ox + bulletX(ln, size))}" y="${y}" fill="${color}">${BULLET}</text>`);
    for (const f of ln.frags) {
      const attrs = [
        `x="${n(ox + f.x)}"`, `y="${y}"`,
        f.b ? 'font-weight="bold"' : '', f.i ? 'font-style="italic"' : '',
        `fill="${f.href ? LINK_COLOR : color}"`, f.href ? 'text-decoration="underline"' : '',
      ].filter(Boolean).join(' ');
      const t = `<text ${attrs}>${esc(f.text)}</text>`;
      const href = f.href && sanitizeHref(f.href);
      out.push(href ? `<a href="${esc(href)}">${t}</a>` : t);
    }
  }
  return `<g font-family="${esc(FONT_FAMILY)}" font-size="${n(size)}" dominant-baseline="middle" xml:space="preserve"${clip ? ` clip-path="url(#${clip})"` : ''}>${out.join('')}</g>`;
}

/** A card's face (spec §13.5), mirroring drawCardFace. */
function cardFace(face: CardFace): string {
  const out: string[] = [];
  for (const t of face.texts) {
    out.push(layoutSvg(t.layout, t.x, t.y, t.color, null));
    if (t.strike) {
      for (const ln of t.layout.lines) {
        if (ln.w > 0) out.push(`<rect x="${n(t.x + ln.x)}" y="${n(t.y + ln.y - 0.5)}" width="${n(ln.w)}" height="1.1" fill="${t.color}"/>`);
      }
    }
  }
  for (const b of face.boxes) {
    const box = `x="${n(b.x)}" y="${n(b.y)}" width="${n(b.size)}" height="${n(b.size)}" rx="3"`;
    if (b.done) {
      const s = b.size;
      out.push(`<rect ${box} fill="${BOX_DONE}"/>`,
        `<polyline points="${pts([[b.x + s * 0.25, b.y + s * 0.52], [b.x + s * 0.43, b.y + s * 0.7], [b.x + s * 0.76, b.y + s * 0.32]])}" fill="none" stroke="#FFFFFF" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>`);
    } else {
      out.push(`<rect ${box} fill="#FFFFFF" stroke="${BOX_BORDER}" stroke-width="1.5"/>`);
    }
  }
  for (const c of face.chips) {
    const [ix, iy] = chipIconAt(c);
    const icon = c.icon === 'check'
      ? `<rect x="${n(ix)}" y="${n(iy)}" width="11" height="11" rx="2.5"/><polyline points="${pts([[ix + 2.8, iy + 5.8], [ix + 4.8, iy + 7.8], [ix + 8.3, iy + 3.6]])}"/>`
      : `<rect x="${n(ix)}" y="${n(iy + 1)}" width="11" height="10" rx="2"/><path d="M${n(ix)} ${n(iy + 4.5)}h11M${n(ix + 3)} ${n(iy)}v2M${n(ix + 8)} ${n(iy)}v2"/>`;
    out.push(
      `<rect x="${n(c.x)}" y="${n(c.y)}" width="${n(c.w)}" height="${n(c.h)}" rx="${n(c.h / 2)}" fill="${c.bg}"/>`,
      `<g fill="none" stroke="${c.fg}" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round">${icon}</g>`,
      `<text x="${n(c.textX)}" y="${n(c.y + c.h / 2)}" font-family="${esc(FONT_FAMILY)}" font-size="${CHIP_FONT_SIZE}" font-weight="bold" dominant-baseline="middle" fill="${c.fg}">${esc(c.text)}</text>`,
    );
  }
  for (const a of face.avatars) {
    out.push(`<circle cx="${n(a.cx)}" cy="${n(a.cy)}" r="${n(a.r)}" fill="${a.color}"${a.ring ? ' stroke="#FFFFFF" stroke-width="2"' : ''}/>`);
    if (a.initials) {
      out.push(`<text x="${n(a.cx)}" y="${n(a.cy)}" font-family="${esc(FONT_FAMILY)}" font-size="${n(a.r * 0.9)}" font-weight="600" text-anchor="middle" dominant-baseline="middle" fill="${a.initials.startsWith('+') ? '#5F6368' : '#FFFFFF'}">${esc(a.initials)}</text>`);
    }
  }
  return out.join('');
}

function shapeBody(o: Extract<BoardObject, { type: 'shape' }>): string {
  const paint = `fill="${o.fill === 'transparent' ? 'none' : o.fill}" stroke="${o.stroke === 'transparent' ? 'none' : o.stroke}" stroke-width="${n(o.strokeWidth)}" stroke-linejoin="round"`;
  const { w, h } = o;
  switch (o.kind) {
    case 'rect': return `<rect width="${n(w)}" height="${n(h)}" ${paint}/>`;
    case 'ellipse': return `<ellipse cx="${n(w / 2)}" cy="${n(h / 2)}" rx="${n(w / 2)}" ry="${n(h / 2)}" ${paint}/>`;
    case 'diamond': return `<polygon points="${pts([[w / 2, 0], [w, h / 2], [w / 2, h], [0, h / 2]])}" ${paint}/>`;
    case 'triangle': return `<polygon points="${pts([[w / 2, 0], [w, h], [0, h]])}" ${paint}/>`;
  }
}

function connector(c: Connector, get: SvgDeps['get']): string {
  const p = connectorPoints(c, get);
  if (p.length < 2) return '';
  let out = `<polyline points="${pts(p)}" fill="none" stroke="${c.stroke}" stroke-width="${CONNECTOR_WIDTH}" stroke-linejoin="round" stroke-linecap="round"/>`;
  if (c.arrowEnd) out += `<polygon points="${pts(arrowPoints(p[p.length - 2]!, p[p.length - 1]!))}" fill="${c.stroke}"/>`;
  if (c.arrowStart) out += `<polygon points="${pts(arrowPoints(p[1]!, p[0]!))}" fill="${c.stroke}"/>`;
  return out;
}

/** An SVG document of `objs` (paint order) covering world rect `bounds`. */
export function buildSvg(objs: readonly BoardObject[], deps: SvgDeps, bounds: Rect): string {
  const defs: string[] = [];
  const body: string[] = [];
  let clips = 0;
  for (const o of objs) {
    if (o.type === 'connector') {
      body.push(connector(o, deps.get));
      continue;
    }
    const deg = (o.rotation * 180) / Math.PI;
    const transform = `translate(${n(o.x)} ${n(o.y)})${deg ? ` rotate(${n(deg)} ${n(o.w / 2)} ${n(o.h / 2)})` : ''}`;
    const parts: string[] = [];
    switch (o.type) {
      case 'sticky':
        parts.push(`<rect y="4" width="${n(o.w)}" height="${n(o.h)}" fill="#000" fill-opacity="0.08"/>`, `<rect width="${n(o.w)}" height="${n(o.h)}" fill="${o.color}"/>`);
        break;
      case 'shape':
        parts.push(shapeBody(o));
        break;
      case 'frame': {
        // A Kanban column (spec §13.6) is a grey lane, its title followed by its card count.
        const column = o.layout === 'column';
        parts.push(
          column
            ? `<rect width="${n(o.w)}" height="${n(o.h)}" rx="12" fill="#F1F2F4"/>`
            : `<rect width="${n(o.w)}" height="${n(o.h)}" fill="#FFFFFF" stroke="#000" stroke-opacity="0.14"/>`,
          `<text y="${-FRAME_TITLE_GAP_PX}" font-family="${esc(FONT_FAMILY)}" font-size="${FRAME_TITLE_PX}" font-weight="${column ? 600 : 500}" fill="#5f5f5f">${esc(o.title || (column ? 'Column' : 'Frame'))}${column ? `<tspan font-weight="500" fill="#9AA0A6"> · ${o.children.length}</tspan>` : ''}</text>`,
        );
        break;
      }
      case 'freehand': {
        const curve = strokeCurve(o);
        const d = `M${n(curve.start[0])} ${n(curve.start[1])}` + (curve.curves.length
          ? curve.curves.map((c) => `C${c.map(n).join(' ')}`).join('')
          : `l0.01 0`);
        parts.push(`<path d="${d}" fill="none" stroke="${o.stroke}" stroke-width="${n(o.strokeWidth)}" stroke-linecap="round" stroke-linejoin="round"/>`);
        break;
      }
      case 'image': {
        const href = deps.imageHref(o.assetHash);
        parts.push(href
          ? `<image href="${esc(href)}" width="${n(o.w)}" height="${n(o.h)}" preserveAspectRatio="none"/>`
          : `<rect width="${n(o.w)}" height="${n(o.h)}" fill="#E9EAEE"/>`);
        break;
      }
      case 'audio': {
        // The player as the canvas draws it (spec §19.1), silent.
        const { radius, button: b, title, wave, right } = AUDIO_LAYOUT;
        const name = o.title || 'Audio';
        const bars = waveBars(o.peaks).map(([x, half]) => `M${n(x)} ${n(wave.cy - half)}V${n(wave.cy + half)}`).join('');
        parts.push(
          `<g transform="scale(${n(o.w / AUDIO.width)} ${n(o.h / AUDIO.height)})" font-family="${esc(FONT_FAMILY)}">`,
          `<rect y="2" width="${AUDIO.width}" height="${AUDIO.height}" rx="${radius}" fill="#000" fill-opacity="0.07"/>`,
          `<rect width="${AUDIO.width}" height="${AUDIO.height}" rx="${radius}" fill="#FFFFFF" stroke="#000" stroke-opacity="0.12"/>`,
          `<circle cx="${b.cx}" cy="${b.cy}" r="${b.r}" fill="#4262FF"/>`,
          `<path d="M${b.cx - 5} ${b.cy - 9}L${b.cx + 9} ${b.cy}L${b.cx - 5} ${b.cy + 9}Z" fill="#FFFFFF"/>`,
          `<text x="${title.x}" y="${title.baseline}" font-size="${title.size}" font-weight="600" fill="#1A1A1A">${esc(name.length > 28 ? `${name.slice(0, 27).trimEnd()}…` : name)}</text>`,
          `<text x="${right}" y="${title.baseline}" font-size="12" font-weight="500" text-anchor="end" fill="#6B6B6B">${formatDuration(o.duration)}</text>`,
          `<path d="${bars}" stroke="#C3C9D6" stroke-width="2.2" stroke-linecap="round"/>`,
          '</g>',
        );
        break;
      }
      case 'card': {
        const r = n(Math.min(CARD.radius, o.w / 2, o.h / 2));
        const clip = `clip${clips++}`;
        defs.push(`<clipPath id="${clip}"><rect width="${n(o.w)}" height="${n(o.h)}" rx="${r}"/></clipPath>`);
        parts.push(
          `<rect y="2" width="${n(o.w)}" height="${n(o.h)}" rx="${r}" fill="#000" fill-opacity="0.07"/>`,
          `<rect width="${n(o.w)}" height="${n(o.h)}" rx="${r}" fill="#FFFFFF" stroke="#000" stroke-opacity="0.12"/>`,
          `<rect width="${n(o.w)}" height="${n(Math.min(CARD.strip, o.h))}" fill="${o.color}" clip-path="url(#${clip})"/>`,
        );
        break;
      }
      case 'text':
        break;
    }
    const placed = deps.layoutOf(o);
    if (placed?.card) {
      parts.push(cardFace(placed.card));
    } else if (placed) {
      let clip: string | null = null;
      if (o.type !== 'text') {
        clip = `clip${clips++}`;
        const r = placed.region;
        defs.push(`<clipPath id="${clip}"><rect x="${n(r.x)}" y="${n(r.y)}" width="${n(r.w)}" height="${n(r.h)}"/></clipPath>`);
      }
      parts.push(text(placed, clip));
    }
    body.push(`<g transform="${transform}">${parts.join('')}</g>`);
  }
  const { x, y, w, h } = bounds;
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${n(w)}" height="${n(h)}" viewBox="${n(x)} ${n(y)} ${n(w)} ${n(h)}">`,
    defs.length ? `<defs>${defs.join('')}</defs>` : '',
    `<rect x="${n(x)}" y="${n(y)}" width="${n(w)}" height="${n(h)}" fill="#FFFFFF"/>`,
    ...body,
    '</svg>',
  ].join('\n');
}
